import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Newsletter from '../components/Newsletter';
import Footer from '../components/Footer';

interface FAQItem {
  question: string;
  answer: string;
}

const Location: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const galleryImages = [
    '/carousel location/8ad66274c7eb11d7caef040cf95dd7ed.jpg',
    '/carousel location/3.png',
    '/carousel location/1.png',
    '/carousel location/2.png',
    '/carousel location/c554265d5beaca5ad50024e685e92541.jpg',
    '/carousel location/30bb9e295854d4596f66b8c49b7b18c3.jpg',
    '/carousel location/garmin.jpg',
    '/carousel location/4af140e303f28e749c6a0a2f25418058.jpg'
  ];

  const faqItems: FAQItem[] = [
    {
      question: "Comment réserver un vélo de route ou un vélo de triathlon chez Podium ?",
      answer: "Vous pouvez réserver directement en ligne ou en contactant la boutique. Nous vérifions ensemble votre taille, vos besoins et les options souhaitées pour vous proposer le vélo le plus adapté. Le retrait se fait ensuite à notre boutique de Suresnes, à proximité immédiate de Paris."
    },
    {
      question: "Quels types de vélos proposez-vous à la location ?",
      answer: "Nous louons des vélos de route carbone, des vélos de triathlon et des vélos gravel haut de gamme. Chaque modèle est réglé selon votre morphologie pour un confort et une performance optimales."
    },
    {
      question: "Où puis-je rouler avec un vélo loué chez PODIUM ?",
      answer: "Vous pouvez rouler partout en Région Parisienne : Chevreuse, Forêt de Rambouillet, Meudon, Bois de Boulogne, Vallée de la Bièvre, ou encore les itinéraires gravel autour de Paris. Nous proposons également des parcours recommandés au départ de la boutique."
    },
    {
      question: "Les vélos sont-ils adaptés aux débutants comme aux cyclistes expérimentés ?",
      answer: "Oui. Nos vélos conviennent aussi bien aux débutants qu'aux cyclistes confirmés. Nous proposons différents modèles selon votre pratique : endurance, performance, triathlon ou gravel. Un réglage personnalisé est systématiquement effectué."
    },
    {
      question: "Quelles sont les options disponibles lors de la location ?",
      answer: "Vous pouvez ajouter : casque, pédales automatiques ou plates, chaussures, compteur GPS, capteur de puissance, prolongateurs, sacoches de voyage, housse de transport, etc. Ces options permettent de rouler immédiatement dans les meilleures conditions."
    },
    {
      question: "Proposez-vous la location pour une course ou un triathlon en Île-de-France ?",
      answer: "Oui, de nombreux cyclistes et triathlètes louent leur vélo chez nous pour des courses telles que le Triathlon de Paris, le Triathlon de Versailles, ou des épreuves en Chevreuse. Nous adaptons le montage selon les exigences de votre course."
    },
    {
      question: "Dois-je laisser une caution pour louer un vélo ?",
      answer: "Oui, une caution est requise au moment du retrait du vélo. Son montant varie selon le modèle choisi. Elle vous est restituée au retour si le matériel est en bon état."
    },
    {
      question: "Peut-on louer un vélo pour plusieurs jours ou plusieurs semaines ?",
      answer: "Oui. Nous proposons des locations à la journée, au week-end, à la semaine et sur demande longue durée. Pour les locations prolongées, contactez-nous pour obtenir un devis personnalisé."
    },
    {
      question: "Quel équipement dois-je apporter pour louer un vélo ?",
      answer: "Vous pouvez venir uniquement avec votre tenue de cyclisme. Nous pouvons fournir pédales, casque, chaussures, outils et accessoires si nécessaire. Si vous avez vos pédales préférées, nous pouvons les installer sur votre vélo."
    },
    {
      question: "Comment se déroule le retrait et le retour du vélo ?",
      answer: "Le retrait se fait à la boutique PODIUM, au 26 Bd Henri Sellier à Suresnes. Nous ajustons ensemble la position et faisons un contrôle complet avant votre départ. Le retour se fait au même endroit, aux horaires d'ouverture."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-black mb-12 leading-tight">
            Location vélo route, course, triathlon à<br />
            Paris
          </h1>

          {/* Main Image */}
          <div className="relative rounded-lg overflow-hidden mb-16 max-w-4xl mx-auto">
            <img
              src="/location velo/Santa-Cruzs-Spokesman-Bicycles-Boutique-1.jpg"
              alt="Boutique vélo Podium - Location"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>

      {/* Content Section with Sidebar */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-black mb-4">
                  Réservez votre vélo route, triathlon ou gravel pour vos sorties en région parisienne
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Envie de découvrir le vélo de route, de préparer un triathlon ou d'explorer les chemins
                  gravel autour de Paris ? <strong>Podium vous propose un service de location de vélos haut de
                    gamme</strong> : vélos de route, vélos de triathlon et gravel en carbone, équipés de composants
                  performants et entièrement réglés à votre morphologie.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Que ce soit pour une course en Île-de-France, un week-end sportif, une préparation
                  d'épreuve, ou une aventure bikepacking, profitez d'un matériel fiable, léger et
                  parfaitement entretenu.
                </p>
              </div>

              <div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  En complément, vous pouvez ajouter différentes <strong>options premium</strong> : compteur GPS,
                  capteur de puissance, prolongateurs, sacoches de voyage, housse de transport et bien
                  plus encore.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Bonus :</strong> Découvrez également notre sélection d'itinéraires vélo route et gravel au départ
                  de la boutique, idéale pour planifier vos sorties en quelques minutes.
                </p>
              </div>

              {/* CTA Button */}
              <div className="pt-4">
                <Link
                  to="/reservation-location"
                  className="inline-block bg-black text-white px-8 py-4 rounded-lg hover:bg-gray-800 transition-colors duration-300 font-medium text-lg"
                >
                  Je réserve mon vélo de location
                </Link>
              </div>
            </div>

            {/* Sidebar - Les Infos Essentielles */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-lg p-6 sticky top-24">
                <h3 className="text-xl font-bold text-black mb-6">Les Infos Essentielles</h3>
                <ul className="space-y-4 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="mr-2">📍</span>
                    <span><strong>Lieu :</strong> 26 Bd Henri Sellier, 92150 Suresnes</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">💰</span>
                    <span><strong>Prix :</strong> À partir de 60€/jour (Classique) ou 90€/jour (Premium)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">🚴</span>
                    <span><strong>Matériel :</strong> Vélo carbone (route, gravel, triathlon)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">⚙️</span>
                    <div>
                      <strong>En Option :</strong>
                      <p className="mt-1">Casque – Chaussures – Pédales automatiques ou plates – Compteur GPS – Capteur de puissance – Roues carbone – Sacoches bikepacking – Housse de transport.</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">📞</span>
                    <span><strong>Contact :</strong> 01 XX XX XX XX</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi louer chez Podium */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - Pourquoi */}
            <div>
              <h2 className="text-2xl font-bold text-black mb-6">
                Pourquoi louer votre vélo chez Podium ?
              </h2>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span><strong>Vélos haut de gamme</strong> – Cadres carbone, groupes Shimano/SRAM</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span><strong>Réglage personnalisé</strong> – Position adaptée à votre morphologie</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span><strong>Options complètes</strong> – Tout l'équipement disponible en option</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span><strong>Idéalement situé</strong> – À 10 min de Paris, départs vers Chevreuse</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span><strong>Conseil d'experts</strong> – L'équipe vous guide selon vos objectifs</span>
                </li>
              </ul>

              <Link
                to="/reservation-location"
                className="inline-block mt-8 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors duration-300 font-medium"
              >
                Réserver maintenant
              </Link>
            </div>

            {/* Right Column - Nos Vélos */}
            <div>
              <h2 className="text-2xl font-bold text-black mb-6">
                Nos vélos disponibles
              </h2>
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center p-2">
                      <img
                        src="/type velo loc/classique.png"
                        alt="Vélo Route Classique"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold text-black">Vélo Route Classique</h3>
                      <p className="text-gray-500 text-sm">Cadre carbone, groupe Shimano 105</p>
                      <p className="text-black font-bold mt-1">À partir de 60€/jour</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 ring-2 ring-amber-400">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-amber-50 rounded-lg flex items-center justify-center p-2">
                      <img
                        src="/type velo loc/premium.png"
                        alt="Vélo Route Premium"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-black">Vélo Route Premium</h3>
                        <span className="bg-amber-400 text-white text-xs px-2 py-0.5 rounded-full font-medium">POPULAIRE</span>
                      </div>
                      <p className="text-gray-500 text-sm">Cadre carbone aéro, groupe Ultegra Di2</p>
                      <p className="text-black font-bold mt-1">À partir de 90€/jour</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center p-2">
                      <img
                        src="/type velo loc/gravel.png"
                        alt="Vélo Gravel"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold text-black">Vélo Gravel</h3>
                      <p className="text-gray-500 text-sm">Cadre carbone, groupe GRX</p>
                      <p className="text-black font-bold mt-1">À partir de 70€/jour</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center p-2">
                      <img
                        src="/type velo loc/triathlon.png"
                        alt="Vélo Triathlon"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold text-black">Vélo Triathlon</h3>
                      <p className="text-gray-500 text-sm">Cadre aéro, prolongateurs, position TT</p>
                      <p className="text-black font-bold mt-1">À partir de 100€/jour</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-black mb-8 text-center">Notre flotte & nos équipements</h2>

          {/* Carousel */}
          <div className="relative max-w-4xl mx-auto mb-12">
            <div className="relative rounded-2xl overflow-hidden aspect-video">
              <img
                src={galleryImages[currentImageIndex]}
                alt={`Location vélo ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
              />

              {/* Navigation Buttons */}
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-4">
              {galleryImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${index === currentImageIndex ? 'w-8 bg-black' : 'bg-gray-300'
                    }`}
                  aria-label={`Aller à l'image ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Grid Gallery - 8 images */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((img, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`rounded-xl overflow-hidden aspect-video shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02] ${index === currentImageIndex ? 'ring-2 ring-black' : ''
                  }`}
              >
                <img
                  src={img}
                  alt={`Location vélo ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Confiance Section - Avis Clients */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              Ils ont loué leur vélo chez Podium
            </h2>
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg key={star} className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <span className="text-xl font-bold text-black">4.8</span>
              <span className="text-gray-500">sur Google</span>
            </div>
          </div>

          {/* Avis Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Avis 1 */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  T
                </div>
                <div>
                  <h4 className="font-semibold text-black">Thomas K.</h4>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-xs text-gray-400 ml-2">il y a 1 semaine</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                "Loué un vélo route Premium pour le week-end. Matériel impeccable, réglages parfaits. J'ai pu faire ma première sortie en Chevreuse sans aucun souci. Équipe au top !"
              </p>
            </div>

            {/* Avis 2 */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  L
                </div>
                <div>
                  <h4 className="font-semibold text-black">Laura M.</h4>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-xs text-gray-400 ml-2">il y a 2 semaines</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                "Location pour le Triathlon de Paris. Vélo tri parfaitement réglé, j'ai même ajouté le capteur de puissance. Service pro et réactif. Je reviendrai !"
              </p>
            </div>

            {/* Avis 3 */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  N
                </div>
                <div>
                  <h4 className="font-semibold text-black">Nicolas B.</h4>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-xs text-gray-400 ml-2">il y a 3 semaines</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                "Gravel loué pour une semaine de bikepacking. J'ai pris les sacoches en option, qualité au rendez-vous. Les conseils sur les itinéraires étaient précieux."
              </p>
            </div>
          </div>

          {/* Google Badge */}
          <div className="mt-10 flex justify-center">
            <button
              onClick={() => window.open('https://www.google.com/maps', '_blank')}
              className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-sm hover:shadow-md transition-all text-gray-700 font-medium cursor-pointer"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Voir tous nos avis sur Google
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-black mb-12">FAQ</h2>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="border-b border-gray-200 pb-4">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full text-left flex justify-between items-center py-2 hover:text-gray-600 transition-colors"
                >
                  <span className="font-medium text-black pr-4">{item.question}</span>
                  <svg
                    className={`w-5 h-5 flex-shrink-0 transform transition-transform ${openFAQ === index ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFAQ === index && (
                  <p className="text-gray-600 mt-2 text-sm leading-relaxed">
                    {item.answer}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </div>
  );
};

export default Location;
